import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { staffApi, errMsg, fmtDate } from "./api";
import { Field, Status } from "./Shell";

const ListChecks = ({ meta, value, onChange, idPrefix }) => (
  <fieldset className="grid gap-2 sm:grid-cols-2">
    <legend className="mb-2 text-sm font-semibold text-navy">Lists this person can download</legend>
    {meta.lists.map((l) => (
      <label key={l.key} className="flex items-start gap-2 text-sm text-ink">
        <input type="checkbox" className="mt-1" checked={value.includes(l.key)}
          onChange={(e) => onChange(e.target.checked ? [...value, l.key] : value.filter((k) => k !== l.key))}
          data-testid={`${idPrefix}-list-${l.key}`} />
        <span>{l.title}{meta.restricted.includes(l.key) && <span className="ml-1 text-xs font-semibold text-gold-deep">(restricted)</span>}</span>
      </label>
    ))}
  </fieldset>
);

const AddStaff = ({ meta, onAdded }) => {
  const blank = { name: "", email: "", role: "secretary", lists: meta.role_lists.secretary };
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState({});
  const setRole = (role) => setForm({ ...form, role, lists: meta.role_lists[role] ?? [] });
  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading" });
    try {
      await staffApi.post("/admin/users", form);
      setForm(blank);
      setStatus({ state: "success", msg: "Account added. A one-time setup link was emailed to them." });
      onAdded();
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5 border border-mist bg-white p-6" data-testid="staff-add-form">
      <p className="font-semibold text-navy">Add a staff member</p>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-add-name-input" />
        <Field label="Work email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-add-email-input" />
      </div>
      <label className="block">
        <span className="mb-2 block text-sm font-semibold text-navy">Role</span>
        <select className="field-input" value={form.role} onChange={(e) => setRole(e.target.value)} data-testid="staff-add-role-select">
          {Object.entries(meta.roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      {form.role === "admin"
        ? <p className="text-sm text-slate">Administrators can access every list, staff accounts and the activity log.</p>
        : <ListChecks meta={meta} value={form.lists} onChange={(lists) => setForm({ ...form, lists })} idPrefix="staff-add" />}
      <Status status={status} testId="staff-add-status" />
      <button type="submit" disabled={status.state === "loading"} className="btn btn-navy disabled:opacity-60" data-testid="staff-add-submit-button">
        {status.state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add and Email Setup Link
      </button>
    </form>
  );
};

const StaffRow = ({ u, meta, me, onChanged }) => {
  const [lists, setLists] = useState(u.lists);
  const [status, setStatus] = useState({});
  const act = async (fn, msg) => {
    setStatus({ state: "loading" });
    try { await fn(); setStatus({ state: "success", msg }); onChanged(); } catch (err) { setStatus({ state: "error", msg: await errMsg(err) }); }
  };
  const slug = u.email.replace(/[^a-z0-9]/gi, "-");
  return (
    <li className="space-y-4 p-5" data-testid={`staff-user-${slug}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-navy">{u.name} <span className="font-normal text-slate">· {u.role_label}{!u.active && " · Deactivated"}</span></p>
        <p className="text-xs text-slate">{u.email}</p>
      </div>
      <p className="text-xs text-slate">
        Password: {u.has_password ? "set" : "not set yet"} · Two-step sign-in: {u.two_factor ? "on" : "not set up yet"} · Recovery codes left: {u.recovery_left}
        {u.last_login_at && ` · Last sign-in: ${fmtDate(u.last_login_at)}`}
      </p>
      {u.role !== "admin" && (
        <>
          <ListChecks meta={meta} value={lists} onChange={setLists} idPrefix={`staff-user-${slug}`} />
          <button type="button" className="btn btn-navy !px-4 !py-2" onClick={() => act(() => staffApi.patch(`/admin/users/${u.id}`, { lists }), "Access saved.")} data-testid={`staff-user-${slug}-save-button`}>Save Access</button>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-ghost !px-4 !py-2" onClick={() => act(() => staffApi.post(`/admin/users/${u.id}/send-link`), "Password link emailed.")} data-testid={`staff-user-${slug}-send-link-button`}>Email Password Link</button>
        {u.two_factor && (
          <button type="button" className="btn btn-ghost !px-4 !py-2" onClick={() => act(() => staffApi.post(`/admin/users/${u.id}/reset-2fa`), "Two-step sign-in reset. They'll set it up again at next sign-in.")} data-testid={`staff-user-${slug}-reset-2fa-button`}>Reset Two-Step Sign-In</button>
        )}
        {u.id !== me.id && (
          <button type="button" className="btn btn-ghost !px-4 !py-2" onClick={() => act(() => staffApi.patch(`/admin/users/${u.id}`, { active: !u.active }), u.active ? "Account deactivated." : "Account reactivated.")} data-testid={`staff-user-${slug}-toggle-active-button`}>{u.active ? "Deactivate" : "Reactivate"}</button>
        )}
      </div>
      <Status status={status} testId={`staff-user-${slug}-status`} />
    </li>
  );
};

export const StaffPanel = ({ meta, me }) => {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    staffApi.get("/admin/users")
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(async (e) => setError(await errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <p className="text-sm text-fire" role="alert">{error}</p>;
  if (!users || !meta) return <Loader2 className="animate-spin text-navy" />;
  return (
    <div className="space-y-8" data-testid="staff-accounts-panel">
      <ul className="divide-y divide-mist border border-mist bg-white">
        {users.map((u) => <StaffRow key={u.id} u={u} meta={meta} me={me} onChanged={load} />)}
      </ul>
      <AddStaff meta={meta} onAdded={load} />
    </div>
  );
};
